import { useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

export type DiffStatus = 'unchanged' | 'added' | 'removed' | 'modified';

export interface WordDiff {
  text: string;
  status: DiffStatus;
}

export interface LineDiff {
  type: DiffStatus;
  lineA?: string;
  lineB?: string;
  lineNumber: {
    a: number | null;
    b: number | null;
  };
  wordDiffs?: WordDiff[];
  isStructural?: boolean;  // True for [VERSE], [CHORUS], {VOCAL PHYSICS} etc.
  structuralType?: 'section' | 'physics' | 'production' | 'metadata';
}

export interface SectionDiff {
  name: string;
  type: 'verse' | 'chorus' | 'bridge' | 'outro' | 'intro' | 'physics' | 'production' | 'other';
  hasChanges: boolean;
  lines: LineDiff[];
  startLineA: number | null;
  startLineB: number | null;
}

export interface DiffResult {
  lines: LineDiff[];
  sections: SectionDiff[];
  stats: DiffStats;
}

export interface DiffStats {
  totalChanges: number;
  additions: number;
  removals: number;
  modifications: number;
  sectionsAffected: number;
  totalSections: number;
}

export interface DiffOptions {
  ignoreWhitespace?: boolean;
  ignoreCase?: boolean;
  detectStructuralElements?: boolean;
  wordLevelDiff?: boolean;
}

// ============================================================================
// Structural Element Detection
// ============================================================================

const SECTION_PATTERNS = {
  verse: /^\[VERSE\s*\d*\]$/i,
  chorus: /^\[CHORUS\s*\d*\]$/i,
  bridge: /^\[BRIDGE\s*\d*\]$/i,
  outro: /^\[OUTRO\]$/i,
  intro: /^\[INTRO\]$/i,
  preChorus: /^\[PRE-?CHORUS\s*\d*\]$/i,
  tag: /^\[TAG\]$/i,
};

const PHYSICS_PATTERN = /^\{(?:LAYER-?\d+\s+)?VOCAL\s+PHYSICS[:\s]/i;
const PRODUCTION_PATTERN = /^\[PRODUCTION\s+NOTES?\]$/i;
const METADATA_PATTERN = /^\[(?:TITLE|ARTIST|KEY|TEMPO|STYLE)\]/i;

/**
 * Detect if a line is a structural element
 * Fix: Renamed 'type' property to 'structuralType' to avoid collision with LineDiff.type
 */
function detectStructuralType(line: string): { isStructural: boolean; structuralType?: LineDiff['structuralType'] } {
  const trimmed = line.trim();
  
  // Check section markers
  for (const [, pattern] of Object.entries(SECTION_PATTERNS)) {
    if (pattern.test(trimmed)) {
      return { isStructural: true, structuralType: 'section' };
    }
  }
  
  // Check vocal physics
  if (PHYSICS_PATTERN.test(trimmed) || (trimmed.startsWith('{') && trimmed.includes('VOCAL'))) {
    return { isStructural: true, structuralType: 'physics' };
  }
  
  // Check production notes
  if (PRODUCTION_PATTERN.test(trimmed)) {
    return { isStructural: true, structuralType: 'production' };
  }
  
  // Check metadata
  if (METADATA_PATTERN.test(trimmed)) {
    return { isStructural: true, structuralType: 'metadata' };
  }
  
  return { isStructural: false };
}

/**
 * Determine section type from a section header line
 */
function getSectionType(line: string): SectionDiff['type'] {
  const trimmed = line.trim().toUpperCase();
  
  if (trimmed.includes('VERSE')) return 'verse';
  if (trimmed.includes('CHORUS')) return 'chorus';
  if (trimmed.includes('BRIDGE')) return 'bridge';
  if (trimmed.includes('OUTRO')) return 'outro';
  if (trimmed.includes('INTRO')) return 'intro';
  if (trimmed.includes('VOCAL PHYSICS') || trimmed.startsWith('{')) return 'physics';
  if (trimmed.includes('PRODUCTION')) return 'production';
  
  return 'other';
}

// ============================================================================
// Core Diff Algorithms
// ============================================================================

/**
 * Longest Common Subsequence for line-level diffing
 * Uses dynamic programming approach
 */
function computeLCS<T>(a: T[], b: T[], equals: (x: T, y: T) => boolean): number[][] {
  const m = a.length;
  const n = b.length;
  
  // Create DP table
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  // Fill the table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (equals(a[i - 1], b[j - 1])) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  return dp;
}

/**
 * Backtrack through LCS table to generate diff
 */
function backtrackLCS<T>(
  dp: number[][],
  a: T[],
  b: T[],
  equals: (x: T, y: T) => boolean
): Array<{ type: 'match' | 'remove' | 'add'; indexA?: number; indexB?: number }> {
  const result: Array<{ type: 'match' | 'remove' | 'add'; indexA?: number; indexB?: number }> = [];
  
  let i = a.length;
  let j = b.length;
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && equals(a[i - 1], b[j - 1])) {
      result.unshift({ type: 'match', indexA: i - 1, indexB: j - 1 });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'add', indexB: j - 1 });
      j--;
    } else if (i > 0) {
      result.unshift({ type: 'remove', indexA: i - 1 });
      i--;
    }
  }
  
  return result;
}

/**
 * Word-level diff for modified lines
 */
function diffWords(textA: string, textB: string, options: DiffOptions = {}): WordDiff[] {
  // Tokenize - preserve punctuation as separate tokens
  const tokenize = (text: string): string[] => {
    return text.split(/(\s+|[.,!?;:'"()\[\]{}])/).filter(t => t.length > 0);
  };
  
  let tokensA = tokenize(textA);
  let tokensB = tokenize(textB);
  
  // Apply options
  const normalize = (t: string): string => {
    let result = t;
    if (options.ignoreWhitespace) result = result.trim();
    if (options.ignoreCase) result = result.toLowerCase();
    return result;
  };
  
  const equals = (a: string, b: string): boolean => normalize(a) === normalize(b);
  
  // Compute LCS
  const dp = computeLCS(tokensA, tokensB, equals);
  const operations = backtrackLCS(dp, tokensA, tokensB, equals);
  
  // Convert operations to WordDiff array
  const result: WordDiff[] = [];
  
  for (const op of operations) {
    if (op.type === 'match') {
      result.push({ text: tokensB[op.indexB!], status: 'unchanged' });
    } else if (op.type === 'remove') {
      result.push({ text: tokensA[op.indexA!], status: 'removed' });
    } else if (op.type === 'add') {
      result.push({ text: tokensB[op.indexB!], status: 'added' });
    }
  }
  
  // Merge adjacent same-status tokens for cleaner output
  return mergeAdjacentWordDiffs(result);
}

/**
 * Merge adjacent word diffs with the same status
 */
function mergeAdjacentWordDiffs(diffs: WordDiff[]): WordDiff[] {
  if (diffs.length === 0) return [];
  
  const merged: WordDiff[] = [];
  let current = { ...diffs[0] };
  
  for (let i = 1; i < diffs.length; i++) {
    const next = diffs[i];
    
    // Merge if same status (but keep whitespace separate for clarity)
    if (next.status === current.status && !current.text.match(/^\s+$/) && !next.text.match(/^\s+$/)) {
      current.text += next.text;
    } else {
      merged.push(current);
      current = { ...next };
    }
  }
  
  merged.push(current);
  return merged;
}

// ============================================================================
// Main Diff Function
// ============================================================================

/**
 * Compute full diff between two texts
 */
export function computeDiff(
  textA: string,
  textB: string,
  options: DiffOptions = {}
): DiffResult {
  const defaultOptions: DiffOptions = {
    ignoreWhitespace: false,
    ignoreCase: false,
    detectStructuralElements: true,
    wordLevelDiff: true,
    ...options,
  };
  
  // Split into lines
  const linesA = textA.split('\n');
  const linesB = textB.split('\n');
  
  // Normalize function for comparison
  const normalize = (line: string): string => {
    let result = line;
    if (defaultOptions.ignoreWhitespace) result = result.trim();
    if (defaultOptions.ignoreCase) result = result.toLowerCase();
    return result;
  };
  
  const equals = (a: string, b: string): boolean => normalize(a) === normalize(b);
  
  // Compute line-level LCS
  const dp = computeLCS(linesA, linesB, equals);
  const operations = backtrackLCS(dp, linesA, linesB, equals);
  
  // Build line diffs
  const lineDiffs: LineDiff[] = [];
  let lineNumA = 0;
  let lineNumB = 0;
  
  // Group consecutive removes and adds for potential modification detection
  let i = 0;
  while (i < operations.length) {
    const op = operations[i];
    
    if (op.type === 'match') {
      const line = linesA[op.indexA!];
      const structural = defaultOptions.detectStructuralElements 
        ? detectStructuralType(line) 
        : { isStructural: false };
      
      lineDiffs.push({
        type: 'unchanged',
        lineA: line,
        lineB: linesB[op.indexB!],
        lineNumber: { a: lineNumA++, b: lineNumB++ },
        ...structural,
      });
      i++;
    } else {
      // Look ahead for remove/add pairs that might be modifications
      const removes: number[] = [];
      const adds: number[] = [];
      
      while (i < operations.length && operations[i].type !== 'match') {
        if (operations[i].type === 'remove') {
          removes.push(operations[i].indexA!);
        } else {
          adds.push(operations[i].indexB!);
        }
        i++;
      }
      
      // Try to pair removes with adds as modifications
      const paired = Math.min(removes.length, adds.length);
      
      for (let p = 0; p < paired; p++) {
        const la = linesA[removes[p]];
        const lb = linesB[adds[p]];
        
        // Fix: Correctly combine structural info from both lines and avoid logic error with truthy objects
        let str;
        if (defaultOptions.detectStructuralElements) {
          const sA = detectStructuralType(la);
          const sB = detectStructuralType(lb);
          str = {
            isStructural: sA.isStructural || sB.isStructural,
            structuralType: sA.structuralType || sB.structuralType
          };
        } else {
          str = { isStructural: false };
        }
        
        const wordDiffs = defaultOptions.wordLevelDiff 
          ? diffWords(la, lb, defaultOptions)
          : undefined;
        
        lineDiffs.push({
          type: 'modified',
          lineA: la,
          lineB: lb,
          lineNumber: { a: lineNumA++, b: lineNumB++ },
          wordDiffs,
          ...str,
        });
      }
      
      // Remaining removes
      for (let r = paired; r < removes.length; r++) {
        const line = linesA[removes[r]];
        const structural = defaultOptions.detectStructuralElements 
          ? detectStructuralType(line)
          : { isStructural: false };
        
        lineDiffs.push({
          type: 'removed',
          lineA: line,
          lineNumber: { a: lineNumA++, b: null },
          ...structural,
        });
      }
      
      // Remaining adds
      for (let a = paired; a < adds.length; a++) {
        const line = linesB[adds[a]];
        const structural = defaultOptions.detectStructuralElements 
          ? detectStructuralType(line)
          : { isStructural: false };
        
        lineDiffs.push({
          type: 'added',
          lineB: line,
          lineNumber: { a: null, b: lineNumB++ },
          ...structural,
        });
      }
    }
  }
  
  // Group into sections
  const sections = groupIntoSections(lineDiffs);
  
  // Calculate stats
  const stats = calculateStats(lineDiffs, sections);
  
  return { lines: lineDiffs, sections, stats };
}

/**
 * Group line diffs into logical sections
 */
function groupIntoSections(lineDiffs: LineDiff[]): SectionDiff[] {
  const sections: SectionDiff[] = [];
  let currentSection: SectionDiff | null = null;
  
  for (const line of lineDiffs) {
    const text = line.lineA || line.lineB || '';
    const isNewSection = line.isStructural && line.structuralType === 'section';
    
    if (isNewSection || !currentSection) {
      // Start new section
      if (currentSection) {
        sections.push(currentSection);
      }
      
      currentSection = {
        name: isNewSection ? text.trim() : 'Header',
        type: getSectionType(text),
        hasChanges: line.type !== 'unchanged',
        lines: [line],
        startLineA: line.lineNumber.a,
        startLineB: line.lineNumber.b,
      };
    } else {
      // Add to current section
      currentSection.lines.push(line);
      if (line.type !== 'unchanged') {
        currentSection.hasChanges = true;
      }
    }
  }
  
  // Push final section
  if (currentSection) {
    sections.push(currentSection);
  }
  
  return sections;
}

/**
 * Calculate diff statistics
 */
function calculateStats(lineDiffs: LineDiff[], sections: SectionDiff[]): DiffStats {
  let additions = 0;
  let removals = 0;
  let modifications = 0;
  
  for (const line of lineDiffs) {
    switch (line.type) {
      case 'added':
        additions++;
        break;
      case 'removed':
        removals++;
        break;
      case 'modified':
        modifications++;
        break;
    }
  }
  
  const sectionsAffected = sections.filter(s => s.hasChanges).length;
  
  return {
    totalChanges: additions + removals + modifications,
    additions,
    removals,
    modifications,
    sectionsAffected,
    totalSections: sections.length,
  };
}

// ============================================================================
// React Hook
// ============================================================================

/**
 * React hook for computing diffs
 */
export function useDiff(
  textA: string | null | undefined,
  textB: string | null | undefined,
  options: DiffOptions = {}
): DiffResult | null {
  return useMemo(() => {
    if (!textA || !textB) return null;
    return computeDiff(textA, textB, options);
  }, [textA, textB, options.ignoreWhitespace, options.ignoreCase, options.detectStructuralElements, options.wordLevelDiff]);
}

/**
 * Hook for comparing specific sections only
 */
export function useSectionDiff(
  textA: string | null | undefined,
  textB: string | null | undefined,
  sectionFilter: SectionDiff['type'][] = [],
  options: DiffOptions = {}
): SectionDiff[] {
  const fullDiff = useDiff(textA, textB, options);
  
  return useMemo(() => {
    if (!fullDiff) return [];
    if (sectionFilter.length === 0) return fullDiff.sections;
    
    return fullDiff.sections.filter(s => sectionFilter.includes(s.type));
  }, [fullDiff, sectionFilter]);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Render word diffs to annotated HTML string (for display)
 */
export function renderWordDiffsToHTML(wordDiffs: WordDiff[]): string {
  return wordDiffs.map(wd => {
    switch (wd.status) {
      case 'added':
        return `<span class="diff-added">${escapeHTML(wd.text)}</span>`;
      case 'removed':
        return `<span class="diff-removed">${escapeHTML(wd.text)}</span>`;
      default:
        return escapeHTML(wd.text);
    }
  }).join('');
}

/**
 * Escape HTML special characters
 */
function escapeHTML(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Get a simple text summary of changes
 */
export function getDiffSummaryText(stats: DiffStats): string {
  const parts: string[] = [];
  
  if (stats.additions > 0) {
    parts.push(`+${stats.additions} added`);
  }
  if (stats.removals > 0) {
    parts.push(`-${stats.removals} removed`);
  }
  if (stats.modifications > 0) {
    parts.push(`~${stats.modifications} modified`);
  }
  
  if (parts.length === 0) {
    return 'No changes';
  }
  
  return `${stats.totalChanges} changes: ${parts.join(', ')} | ${stats.sectionsAffected}/${stats.totalSections} sections affected`;
}

export default useDiff;
